<template>
  <nav class="hidden md:flex mb-4 h-8" aria-label="Breadcrumb">
    <ol role="list" class="flex space-x-4 rounded-md bg-white px-6 shadow">
      <li class="flex">
        <div class="flex items-center">
        <router-link to="/hub" class="transition ease-in-out duration-300 text-gray-400 hover:text-gray-500">
          <HomeIcon class="h-5 w-5 flex-shrink-0" aria-hidden="true" />
          <span class="sr-only">Home</span>
        </router-link>
        </div>
      </li>
      <li class="flex">
        <div class="flex items-center">
        <svg class="h-full w-6 flex-shrink-0 text-gray-200" viewBox="0 0 24 44" preserveAspectRatio="none" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M.293 0l22 22-22 22h1.414l22-22-22-22H.293z" />
        </svg>
        <router-link to="/dashboard/storage" class="transition ease-in-out duration-300 ml-4 text-xs font-medium text-gray-500 hover:text-gray-700" aria-current="page">Moduł magazyn</router-link>
        </div>
      </li>
      <li class="flex">
        <div class="flex items-center">
        <svg class="h-full w-6 flex-shrink-0 text-gray-200" viewBox="0 0 24 44" preserveAspectRatio="none" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M.293 0l22 22-22 22h1.414l22-22-22-22H.293z" />
        </svg>
        <div class="ml-4 text-xs font-medium text-gray-400 cursor-default" aria-current="page">Skaner kodów QR</div>
        </div>
      </li>
    </ol>
  </nav>
  <router-link to="/dashboard/storage" class="block md:hidden transition ease-in-out duration-300 mb-2 text-sm font-medium text-blue-500 hover:text-blue-700" aria-current="page">
    &larr; Cofnij
  </router-link>
  <div :class="[!fullscreen ? 'flex flex-col justify-between gap-4 mt-10' : '', 'flex justify-center']">
    <!-- camera error alert. -->
    <div v-if="error" class="border-l-4 border-red-400 bg-red-50 p-4 mb-4 select-none">
      <div class="flex">
        <div class="flex items-center">
          <ExclamationIcon class="h-6 w-6 text-red-400" aria-hidden="true" />
        </div>
        <div class="ml-3">
          <p class="text-sm text-red-700">
            ERROR:
            {{ ' ' }}
            <span class="font-medium text-red-700 underline hover:text-red-600">{{errorLog.log}}</span>
          </p>
        </div>
      </div>
    </div>

    <div v-if="!fullscreen" class="flex flex-col justify-center items-center gap-4 mt-8">
      <span class="uppercase text-gray-500 font-bold text-2xl select-none text-center">Wybierz tryb do skanowania</span>
      <div class="flex gap-10">
        <div @click="fullscreen = false" class="flex flex-col items-center group text-gray-400">
          <QrcodeIcon :class="[fullscreen != null ? !fullscreen ? 'text-indigo-600' : '' : '', 'w-10 h-10 group-hover:text-gray-600 transition-all duration-300']" />
          <span :class="[fullscreen != null ? !fullscreen ? 'text-indigo-600' : '' : '', 'font-semibold text-lg select-none uppercase group-hover:text-gray-600 transition-all duration-300']">Skaner</span>
        </div>
        <div @click="error ? '' : fullscreen = true" :class="[error ? 'opacity-30 pointer-events-none' : '', 'flex flex-col items-center group text-gray-400']">
          <CameraIcon :class="[fullscreen != null ? fullscreen ? 'text-indigo-600' : '' : '', 'w-10 h-10 group-hover:text-gray-600 transition-all duration-300']" />
          <span :class="[fullscreen != null ? fullscreen ? 'text-indigo-600' : '' : '', 'font-semibold text-lg select-none uppercase group-hover:text-gray-600 transition-all duration-300']">Kamera</span>
        </div>
      </div>
      <p class="text-xs italic text-gray-600 select-none">Skaner działa tylko z urządzeniami z podczerwienią.</p>
      <div @click="clearData('scanner')" :class="[scannerDecode.length > 0 ? 'select-none rounded-md bg-indigo-200 px-3.5 py-2.5 text-sm font-semibold text-indigo-600 shadow-sm hover:bg-indigo-100 items-center text-center flex justify-center my-2' : '', 'transition-all duration-300']">
        <span v-if="scannerDecode.length > 0" class="absolute font-semibold text-lg">INNA PACZKA</span>
        <input v-if="fullscreen != null ? !fullscreen : false" @focus="focus = true" @blur="focus = false" type="text" v-model="scannerDecode" ref="scanner" name="scanner" id="scanner" style="caret-color: transparent;" class="opacity-0 block w-full rounded-md border-0 py-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6" />
      </div>
    </div>

    <!-- camera -->
    <div v-if="fullscreen">
      <div class="flex flex-col p-2">
        <div :class="{'pt-4': error}">
          <div :class="{ fullscreen: fullscreen }" ref="wrapper" @fullscreenchange="onFullscreenChange">
            <qrcode-stream v-if="fullscreen" @detect="onDecode" @init="onInit" @click="this.$refs.scanner.focus()">
              <input ref="scanner" v-model="scannerDecode" type="text" class="hidden">
              <div v-if="!error">
                <button v-if="!notification.success" @click="fullscreen = false" :class="[fullscreen ? 'rotate-180' : '','filter fullscreen-button bg-white rounded-lg hover:text-gray-400 transition-all duration-300 transform -scale-x-100']">
                  <ExternalLinkIcon class="w-6 h-6 bg-transparent" />
                </button>
              </div>

              <div v-if="fullscreen">
                <!-- custom notification -->
                <Transition name="fade2">
                <div v-if="fullscreen && notification.show" :class="{ fullscreen: fullscreen }" class="shadow-lg">
                  <div class="flex min-h-full items-end p-4">
                    <div :class="[notification.success ? 'bg-green-50' : 'bg-red-50', 'rounded-md p-4 w-full']">
                      <div class="flex">
                        <div class="flex-shrink-0">
                          <CheckCircleIcon :class="[notification.success ? 'text-green-400' : 'text-red-400', 'h-5 w-5']" aria-hidden="true" />
                        </div>
                        <div class="ml-3">
                          <h3 :class="[notification.success ? 'text-green-800' : 'text-red-800', 'text-sm font-medium']">{{ notification.head }}</h3>
                          <div :class="[notification.success ? ' text-green-700' : ' text-red-700', 'mt-1 text-sm']">
                            <p>{{ notification.subheader }}</p>
                          </div>
                        </div>
                        <div class="ml-auto pl-3">
                          <div class="-mx-1.5 -my-1.5">
                            <button @click.prevent="notification.show = false;" type="button" :class="[notification.success ? 'bg-green-50 text-green-500 hover:bg-green-100 focus:ring-green-600 focus:ring-offset-green-50' : 'bg-red-50 text-red-500 hover:bg-red-100 focus:ring-red-600 focus:ring-offset-red-50', 'inline-flex rounded-md  p-1.5  focus:outline-none focus:ring-2focus:ring-offset-2']">
                              <span class="sr-only">Dismiss</span>
                              <XIcon class="h-5 w-5" aria-hidden="true" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                </Transition>
                <!-- 1st step -->
                <Transition name="fade">
                  <div :class="{ fullscreen: fullscreen }" class="relative" v-if="modalData.accept && step === 1">
                    <template enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                      <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                    </template>
                    <div :class="[fullscreen ? 'fullscreen' : 'flex justify-center w-full', 'overflow-y-auto shadow-lg']">
                      <div :class="['flex min-h-full items-end justify-center text-center sm:items-center p-4 sm:p-0']">
                        <div class="min-w-full sm:min-w-0 fullscreen-z relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-md transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
                          <div v-if="modalData.found">
                            <div class="text-center select-none">
                              <div v-if="isUnique" class="pb-4">
                                  <div class="rounded-md bg-red-50 p-3 flex justify-center flex-col">
                                    <div class="flex justify-center">
                                      <div class="flex-shrink-0">
                                        <ExclamationIcon class="h-7 w-7 text-red-400 mr-2" aria-hidden="true" />
                                      </div>
                                      <h3 class="font-semibold text-red-800 whitespace-nowrap">WYKRYTO NIEAKTUALNĄ ETYKIETĘ</h3>
                                    </div>
                                    <p class="font-semibold uppercase text-sm">Proszę o wydrukowanie aktualnej etykiety.</p>
                                  </div>
                              </div>
                              <div :class="[isUnique ? 'opacity-50' : '']">
                                <h3 class="text-base font-semibold leading-6 text-gray-900">
                                  {{modalData.bodyText}}
                                </h3>
                                <div v-if="modalData.headerText.length > 0" class="mt-2">
                                  <p class="text-sm text-gray-500">Paczka: 
                                    <span class="font-bold ">{{modalData.headerText}}</span>
                                  </p>
                                </div>
                              </div>
                            </div>
                            <div :class="[isUnique ? 'opacity-50' : '', 'mx-auto flex items-center justify-center']">
                              <img v-if="modalData.found" :class="modalData.image.url.length > 0 ? 'h-40 my-4' : 'h-32 my-4'" :src="modalData.image.url.length > 0 ? modalData.image.url : `https://api.dicebear.com/7.x/initials/svg?seed=${modalData.bodyText}`" alt="">
                            </div>
                          </div>
                          <div v-if="!modalData.found">
                            <div class="mt-3 text-center sm:mt-5 select-none">
                              <h3 class="text-base font-semibold leading-6 text-gray-900">
                                {{modalData.bodyText}}
                              </h3>
                              <div v-if="modalData.headerText.length > 0" class="mt-2">
                                <p class="text-sm text-gray-500">Paczka: 
                                  <span class="font-bold">{{modalData.headerText}}</span>
                                </p>
                              </div>
                            </div>
                            <div class="mx-auto flex items-center justify-center">
                              <component :is="modalData.image" v-if="!modalData.found" class="h-40 text-indigo-500" />
                            </div>
                          </div>
                          <div class="mt-5 sm:mt-6 flex gap-2 select-none">
                            <button @click.prevent="modalData.accept = false, clearData('camera');" type="button" :class="['transition-all duration-300 w-full flex justify-center items-center gap-x-2 rounded-md bg-red-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus:ring-2 focus:ring-red-600 focus:ring-offset-2']">
                              <LogoutIcon class="transform -scale-x-100 -ml-0.5 h-5 w-5" aria-hidden="true" />
                              Anuluj
                            </button>
                            <button :disabled="isUnique" v-if="modalData.found" @click.prevent="changeStep(2)" type="button" :class="[isUnique ? 'opacity-20 pointer-events-none' : '', 'transition-all duration-300 w-full flex justify-center items-center gap-x-2 rounded-md bg-indigo-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2']">
                              <CheckCircleIcon class="-ml-0.5 h-5 w-5" aria-hidden="true" />
                              Przejdź dalej
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Transition>
                <!-- 2nd step -->
                <Transition name="fade">
                <div class="relative z-10" v-if="modalData.accept && step === 2">
                  <template enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                    <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                  </template>
                  <div class="fixed inset-0 z-50 w-screen overflow-y-auto shadow-lg">
                    <div class="flex min-h-full items-end justify-center p-4 text-center sm:p-0">
                      <div class="min-w-full sm:min-w-0 z-50 relative transform overflow-hidden rounded-lg bg-white shadow-md transition-all sm:my-8 sm:w-full sm:max-w-sm">
                        <div class="select-none text-left">
                          <div :class="['flex min-h-full items-end justify-center text-center sm:items-center']">
                            <div v-if="downloadingData" class="min-w-full sm:min-w-0 fullscreen-z relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-md transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
                              <LoadingSpinnerBlue class="py-12 my-12" />
                            </div>
                            <div v-else class="min-w-full sm:min-w-0 fullscreen-z relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-md transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
                              <div class="mt-5">
                                <div class="flex flex-col items-center justify-center gap-4">
                                  <div class="flex items-center gap-2">
                                    <span class="text-sm text-gray-500 font-semibold uppercase">Magazyn:</span>
                                    <span class="inline-flex items-center rounded-md bg-blue-100 px-1.5 py-1 text-sm font-semibold text-blue-700 w-32 justify-center">
                                      {{ package?.storage && package.storage.name ? package.storage.name : 'Nie znaleziono' }}
                                    </span>
                                  </div>
                                  <div class="flex items-center gap-2">
                                    <span class="text-sm text-gray-500 font-semibold uppercase">Segment:</span>
                                  <span class="inline-flex items-center rounded-md bg-blue-100 px-1.5 py-1 text-sm font-semibold text-blue-700 w-32 justify-center">
                                      {{ package?.segment && package.segment.name ? package.segment.name : 'Nie znaleziono' }}
                                    </span>
                                  </div>
                                  <div class="mt-4 flex flex-col items-center">
                                    <span class="text-xs text-gray-400">Sprawdź lokalizację paczki powyżej</span>
                                    <span class="text-xs text-gray-400">Aby zeskanować kolejną paczkę, kliknij poniżej</span>
                                  </div>
                                </div>
                              </div>
                              <div class="mt-5 sm:mt-6 flex gap-2 select-none">
                                <button @click.prevent="modalData.accept = false; package = null; selected = null;" type="button" :class="['transition-all duration-300 w-full flex justify-center items-center gap-x-2 rounded-md bg-blue-500 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-600 focus:ring-2 focus:ring-blue-600 focus:ring-offset-2']">
                                  <LogoutIcon class="transform -scale-x-100 -ml-0.5 h-5 w-5" aria-hidden="true" />
                                  Sprawdź kolejny kod
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                </Transition>
              </div>
            </qrcode-stream>
          </div>
        </div>
      </div>
    </div>
    <div v-else>
      <!-- 1st step !fullscreen -->
      <Transition name="fade">
      <div v-if="modalData.accept && step === 1">
        <template enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
          <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </template>
        <div :class="['flex min-h-full items-end justify-center text-center sm:items-center']">
          <div class="min-w-full sm:min-w-0 fullscreen-z relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-md transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
            <div v-if="modalData.found">
              <div class="text-center select-none">
                <div v-if="isUnique" class="pb-4">
                    <div class="rounded-md bg-red-50 p-3 flex justify-center flex-col">
                      <div class="flex justify-center">
                        <div class="flex-shrink-0">
                          <ExclamationIcon class="h-7 w-7 text-red-400 mr-2" aria-hidden="true" />
                        </div>
                        <h3 class="font-semibold text-red-800">WYKRYTO NIEAKTUALNĄ ETYKIETĘ</h3>
                      </div>
                      <p class="font-semibold uppercase text-sm">Proszę o wydrukowanie aktualnej etykiety.</p>
                    </div>
                </div>
                <div :class="[isUnique ? 'opacity-50' : '']">
                  <h3 class="text-base font-semibold leading-6 text-gray-900">
                    {{modalData.bodyText}}
                  </h3>
                  <div v-if="modalData.headerText.length > 0" class="mt-2">
                    <p class="text-sm text-gray-500">Paczka: 
                      <span class="font-bold ">{{modalData.headerText}}</span>
                    </p>
                  </div>
                </div>
              </div>
              <div :class="[isUnique ? 'opacity-50' : '', 'mx-auto flex items-center justify-center']">
                <img v-if="modalData.found" :class="modalData.image.url.length > 0 ? 'h-40 my-4' : 'h-32 my-4'" :src="modalData.image.url.length > 0 ? modalData.image.url : `https://api.dicebear.com/7.x/initials/svg?seed=${modalData.bodyText}`" alt="">
              </div>
            </div>
            <div v-if="!modalData.found">
              <div class="mt-3 text-center sm:mt-5 select-none">
                <h3 class="text-base font-semibold leading-6 text-gray-900">
                  {{modalData.bodyText}}
                </h3>
                <div v-if="modalData.headerText.length > 0" class="mt-2">
                  <p class="text-sm text-gray-500">Paczka: 
                    <span class="font-bold">{{modalData.headerText}}</span>
                  </p>
                </div>
              </div>
              <div class="mx-auto flex items-center justify-center">
                <component :is="modalData.image" v-if="!modalData.found" class="h-40 text-indigo-500" />
              </div>
            </div>
            <div class="mt-5 sm:mt-6 flex gap-2 select-none">
              <button @click.prevent="modalData.accept = false, clearData('scanner');" type="button" :class="['transition-all duration-300 w-full flex justify-center items-center gap-x-2 rounded-md bg-red-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus:ring-2 focus:ring-red-600 focus:ring-offset-2']">
                <LogoutIcon class="transform -scale-x-100 -ml-0.5 h-5 w-5" aria-hidden="true" />
                Anuluj
              </button>
              <button :disabled="isUnique" v-if="modalData.found" @click.prevent="changeStep(2)" type="button" :class="[isUnique ? 'opacity-20 pointer-events-none' : '', 'transition-all duration-300 w-full flex justify-center items-center gap-x-2 rounded-md bg-indigo-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2']">
                <CheckCircleIcon class="-ml-0.5 h-5 w-5" aria-hidden="true" />
                Przejdź dalej
              </button>
            </div>
          </div>
        </div>
      </div>
      </Transition>
      <!-- 2nd step !fullscreen -->
      <Transition name="fade">
      <div class="relative z-10" v-if="modalData.accept && step === 2">
        <template enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
          <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </template>
        <div :class="['flex min-h-full items-end justify-center text-center sm:items-center']">
          <div v-if="downloadingData" class="min-w-full sm:min-w-0 fullscreen-z relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-md transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
            <LoadingSpinnerBlue class="py-12 my-12" />
          </div>
          <div v-else class="min-w-full sm:min-w-0 fullscreen-z relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-md transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
            <div class="mt-5">
              <div class="flex flex-col items-center justify-center gap-4">
                <div class="flex items-center gap-2">
                  <span class="text-sm text-gray-500 font-semibold uppercase">Magazyn:</span>
                  <span class="inline-flex items-center rounded-md bg-blue-100 px-1.5 py-1 text-sm font-semibold text-blue-700 w-32 justify-center">
                    {{ package?.storage && package.storage.name ? package.storage.name : 'Nie znaleziono' }}
                  </span>
                </div>
                <div class="flex items-center gap-2">
                  <span class="text-sm text-gray-500 font-semibold uppercase">Segment:</span>
                 <span class="inline-flex items-center rounded-md bg-blue-100 px-1.5 py-1 text-sm font-semibold text-blue-700 w-32 justify-center">
                    {{ package?.segment && package.segment.name ? package.segment.name : 'Nie znaleziono' }}
                  </span>
                </div>
                <div class="mt-4 flex flex-col items-center">
                  <span class="text-xs text-gray-400">Sprawdź lokalizację paczki powyżej</span>
                  <span class="text-xs text-gray-400">Aby zeskanować kolejną paczkę, kliknij poniżej</span>
                </div>
              </div>
            </div>
            <div class="mt-5 sm:mt-6 flex gap-2 select-none">
              <button @click.prevent="modalData.accept = false; this.$refs.scanner.focus();" type="button" :class="['transition-all duration-300 w-full flex justify-center items-center gap-x-2 rounded-md bg-blue-500 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-600 focus:ring-2 focus:ring-blue-600 focus:ring-offset-2']">
                <LogoutIcon class="transform -scale-x-100 -ml-0.5 h-5 w-5" aria-hidden="true" />
                Sprawdź kolejny kod
              </button>
            </div>
          </div>
        </div>
      </div>
      </Transition>
    </div>
  </div>
</template>

<script>
import {db} from "@/firebase/gfbconf.js";
import { QrcodeStream } from 'vue3-qrcode-reader'
import LoadingSpinnerBlue from '../components/LoadingSpinnerBlue.vue';
import { Menu, MenuButton, MenuItem, MenuItems, Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue';
import { Listbox, ListboxButton, ListboxLabel, ListboxOption, ListboxOptions } from '@headlessui/vue'
import { QuestionMarkCircleIcon, HomeIcon, CheckIcon, CameraIcon, ChevronDownIcon, CheckCircleIcon, ArrowCircleLeftIcon, EyeIcon, EyeOffIcon, QrcodeIcon, ExclamationIcon, XIcon, ExternalLinkIcon, ChevronDoubleRightIcon } from '@heroicons/vue/solid';
import { LogoutIcon } from '@heroicons/vue/outline';

import axios from 'axios';
import moment from 'moment';
moment.locale('pl')

export default {
  components: {
    Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot, QuestionMarkCircleIcon, LogoutIcon, ExternalLinkIcon, XIcon, ExclamationIcon, QrcodeIcon, LoadingSpinnerBlue, EyeIcon, EyeOffIcon, ChevronDownIcon, CheckCircleIcon, ArrowCircleLeftIcon, Listbox, ListboxButton, ListboxLabel, ListboxOption, ListboxOptions, Menu, MenuButton, MenuItem, MenuItems, QrcodeStream, HomeIcon, CheckIcon, CameraIcon, ChevronDoubleRightIcon
  },
  data () {
    return {
      step: 1,
      isUnique: false,
      focus: false,
      watchers: {
        error: null,
      },
      moment: moment,
      error: false,
      errorLog: {
        name: '',
        log: '',
        block: false
      },
      loading: true,
      modalData: {
        accept: false,
        found: false,
        headerText: '',
        bodyText: '',
        product: null,
        num: null,
      },
      scannerDecode: "",
      fullscreen: null,
      notification: {
        show: false,
        head: "",
        subheader: "",
        success: false,
      },
      resultDecode: null,
      package: null,
      selected: { 
        variantId: null,
        productId: null,
        uniqueId: null,
        num: '1',
        part: '1',
      },
      scanned: {
        warehouse: null,
        segment: null,
        package: '',
      },
      downloadingData: false,
    }
  },
  methods: {
    changeStep(val, exists)
    {
      switch(val)
      {
        case 1:
          this.step = 1;
          break;
        case 2:
          
          let n = this.resultDecode.lastIndexOf('-');
          if(n > -1)
          {
              let customPartId = this.resultDecode.substring(n+1)

              // num of part /n variant id
              let customResult = this.resultDecode.slice(0, n);
              let l = customResult.lastIndexOf('_');
              let numOfPackage = customResult.substring(l+1);
              let variantId = customResult.slice(0, l);
              if(numOfPackage == null) throw 'BRAK NUMERU PACZKI.';

              //product id
              let num = variantId.lastIndexOf('_');
              let productId = variantId.substring(num+1)
              if(productId == null) throw 'BRAK ID PRODUKTU.';
              
              this.selected = {
                variantId: variantId,
                productId: productId,
                uniqueId: customPartId,
                num: numOfPackage,
              }
              
          }
          
          this.downloadPackageInformation();
          this.step = 2;
          break;
      }
    },
    async downloadPackageInformation()
    {
      try {
        this.downloadingData = true;
        const res = await axios.post(`${this.$store.state.apiLink}/scan/locate`, {
          selected: this.selected 
        });
        if(res.data.success)
        {
          this.package = res.data.package;
          setTimeout(() => {
            this.downloadingData = false;
          }, 1500);
        }
        else
        {
          this.package = null;
          setTimeout(() => {
            this.downloadingData = false;
          }, 1500);
        }
      } catch (error)
      {
        console.log(error);  
      }
    },
    clearData(type)
    {
      this.step = 1;
      if(type === 'camera')
      {
        this.scannerDecode = '';
        this.error = false;
      }
      else if(type === 'scanner')
      {
        this.error = false;
        this.scannerDecode = '';
        this.$refs.scanner.readOnly = false;
        this.$refs.scanner.focus();
      }
      this.isUnique = false;
      this.modalData =  {
        accept: false,
        found: false,
        headerText: '',
        bodyText: '',
        product: null,
        num: null,
      };
      this.notification = {
        show: false,
        head: "",
        subheader: "",
        success: false,
      };
      this.selected = {
        variantId: null,
        productId: null,
        uniqueId: null,
        num: '1',
        part: '1',
      };
      this.errorLog = {
        name: '',
        log: ''
      };
    },
    onFullscreenChange(event) {
      // This becomes important when the user doesn't use the button to exit
      // fullscreen but hits ESC on desktop, pushes a physical back button on
      // mobile etc.

      this.fullscreen = document.fullscreenElement != null;
    },
    exitFullscreen() {
      if (!document.fullscreenElement) {
        return;
      }

      if (!document.hidden) {
        if (document.exitFullscreen) {
          document.exitFullscreen()
        } else if (document.mozCancelFullScreen) {
          /* Firefox */
          document.mozCancelFullScreen()
        } else if (document.webkitExitFullscreen) {
          /* Chrome, Safari and Opera */
          document.webkitExitFullscreen()
        } else if (document.msExitFullscreen) {
          /* IE/Edge */
          document.msExitFullscreen()
        }
      }
    },
    requestFullscreen() {
      const elem = this.$refs.wrapper
      this.watchers.error = this.$watch('error', (newVal, oldVal) => {
        if(newVal) {
          this.error = true;
          this.fullscreen = false;
        }
      });
      while(elem != null)
      {
        if (elem.requestFullscreen) {
          elem.requestFullscreen()
        } else if (elem.mozRequestFullScreen) {
          /* Firefox */
          elem.mozRequestFullScreen()
        } else if (elem.webkitRequestFullscreen) {
          /* Chrome, Safari and Opera */
          elem.webkitRequestFullscreen()
        } else if (elem.msRequestFullscreen) {
          /* IE/Edge */
          elem.msRequestFullscreen()
        }
        elem = elem.parentElement;
      };
    },
    async onInit (promise)
    {
      try {
        let res = await promise
      } catch(err)
      {
        this.error = true;
        if(err.name === 'NotAllowedError')
        {
          this.errorLog = {
            name: 'NotAllowedError',
            log: "Musisz przyznać uprawnienia dostępu do kamery.",
          }
        }
        else if (err.name === 'NotFoundError')
        {
          this.errorLog = {
            name: 'NotFoundError',
            log: "Brak kamery w tym urządzeniu."
          }
        }
        else if (err.name === 'NotSupportedError')
        {
          this.errorLog = {
            name: 'NotSupportedError',
            log: "Wymagany bezpieczny kontekst (HTTPS, localhost)."
          }
        }
        else if (err.name === 'NotReadableError')
        {
          this.errorLog = {
            name: 'NotReadableError',
            log: "Kamera jest zajęta."
          }
        }
        else if (err.name === 'OverconstrainedError')
        {
          this.errorLog = {
            name: 'OverconstrainedError',
            log: "Brak kamery spełniającej wymagania."
          }
        }
        else if (err.name === 'StreamApiNotSupportedError')
        {
          this.errorLog = {
            name: 'StreamApiNotSupportedError',
            log: "Stream API nie jest obsługiwany w tej przeglądarce."
          }
        }
        else if (err.name === 'InsecureContextError')
        {
          this.errorLog = {
            name: 'InsecureContextError',
            log: "Dostęp do kamery jest dozwolony tylko w bezpiecznym kontekście. Należy używać protokołu HTTPS."
          }
        }
        else
        {
          this.errorLog = {
            name: err.name,
            log: err.name,
          }
        }
      }
    },
    async onDecode(decode)
    {
      try {
        let decodedResult = await decode;
        if(decodedResult.content.length === 0) throw 'ERROR: The length of the result is equal to 0;';

        this.clearData('camera');
        this.resultDecode = decodedResult.content;
        let str = this.resultDecode;
        let result = null;
        let numOfPackage  = null;

        //varaint id with number of package to show which package is scanned.

        let n = str.lastIndexOf('-');
        if(n !== -1)
        {
          this.isUnique = false;
          result = str.slice(0, n);
          let p = result.lastIndexOf('_');
          if(p === -1) throw 'ERROR;'
          numOfPackage = result.substring(p+1);
        }
        else
        {
          this.isUnique = true;
          let p = str.lastIndexOf('_');
          if(p === -1) throw 'ERROR;'
          numOfPackage = str.substring(p+1);
        }

        const res = await axios.post(`${this.$store.state.apiLink}/scan/checkIfCorrect`, {
          id: this.resultDecode,
        })
        if(res.data.success)
        {
          this.selected.num = numOfPackage;
          this.modalData = {
            accept: true,
            found: true,
            bodyText: res.data.variant.name,
            headerText: `${res.data.variant.num + '/' + res.data.variant.amountOfPackages}`,
            image: res.data.variant.image,
            product: res.data.product,
          }
        }
        else if(res.data.variant == null)
        {
          this.modalData = {
            accept: true,
            found: false,
            bodyText: 'BRAK DANYCH',
            headerText: ``,
            image: QuestionMarkCircleIcon,
            product: null,
          }
        }
        else
        {
          this.modalData = {
            accept: true,
            found: false,
            bodyText: 'BRAK DANYCH',
            headerText: '',
            image: QuestionMarkCircleIcon,
            product: null,
          }
        }

      }
      catch (error)
      {
        console.log(error);
      }
    },
    async onDecodeScanner(val)
    {
      try {
        this.clearData('scanner');
        this.resultDecode = val;
        let str = this.resultDecode;
        let result = null;
        let numOfPackage  = null;

        //varaint id with number of package to show which package is scanned.
        let n = str.lastIndexOf('-');
        if(n !== -1)
        {
          this.isUnique = false;
          result = str.slice(0, n);
          let p = result.lastIndexOf('_');
          if(p === -1) throw 'ERROR;'
          numOfPackage = result.substring(p+1);
        }
        else
        {
          this.isUnique = true;
          let p = str.lastIndexOf('_');
          if(p === -1) throw 'ERROR;'
          numOfPackage = str.substring(p+1);
        }
        
        const res = await axios.post(`${this.$store.state.apiLink}/scan/checkIfCorrect`, {
          id: this.resultDecode,
        })
        if(res.data.success)
        {
          this.selected.num = numOfPackage;
          this.modalData = {
            accept: true,
            found: true,
            bodyText: res.data.variant.name,
            headerText: `${res.data.variant.num + '/' + res.data.variant.amountOfPackages}`,
            image: res.data.variant.image,
            product: res.data.product,
          }
        }
        else
        {
          this.modalData = {
            accept: true,
            found: false,
            bodyText: 'BRAK DANYCH',
            headerText: ``,
            image: QuestionMarkCircleIcon,
            product: null,
          }
        }
      }
      catch (error) {
        console.log(error);  
      }
    },
  },
  watch: {
    focus(isFocused)
    {
      if(!this.modalData.accept)
      {
        if(!isFocused)
        {
          this.$refs.scanner.focus();
        }
      }
    },
    scannerDecode(newVal)
    {
      if(newVal)
      {
        let input = this.$refs.scanner;
        if(newVal.length === 0)
        {
          input.readOnly = false;
        }
        else if(newVal.length > 1)
        {
          input.readOnly = true;
          this.onDecodeScanner(newVal);
        }
      }
    },
    fullscreen(enterFullscreen) {
      if(enterFullscreen)
      {
        this.requestFullscreen()
      }
      else
      {
        this.exitFullscreen();
        this.$nextTick(() => {
          this.$refs.scanner.focus(); 
        })
      }
    },
  }
}
</script>
<style scoped>
.qr-container {
  background:
    linear-gradient(to right, grey 2px, transparent 2px) 0 0,
    linear-gradient(to right, grey 2px, transparent 2px) 0 100%,
    linear-gradient(to left, grey 2px, transparent 2px) 100% 0,
    linear-gradient(to left, grey 2px, transparent 2px) 100% 100%,
    linear-gradient(to bottom, grey 2px, transparent 2px) 0 0,
    linear-gradient(to bottom, grey 2px, transparent 2px) 100% 0,
    linear-gradient(to top, grey 2px, transparent 2px) 0 100%,
    linear-gradient(to top, grey 2px, transparent 2px) 100% 100%;
  background-repeat: no-repeat;
  background-size: 20px 20px;
}

.fade-enter-active{
  transition: opacity 1s ease;
}
.fade-leave-active{
  transition: opacity 0s;
  display: none;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.fade2-enter-active,
.fade2-leave-active {
  transition: opacity 0.5s ease;
}

.fade2-enter-from,
.fade2-leave-to {
  opacity: 0;
}

.fullscreen {
  position: fixed;
  z-index: 1;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
}
.fullscreen-button{
  position: absolute;
  z-index: 2000;
  top: 0;
  right: 0;
  margin: 1rem;
}

.fullscreen-notification {
  position: absolute;
  z-index: 3000;
  top: 0;
  left: 0;
  margin: 1rem;
}

.fullscreen-button-check {
  position: absolute;
  z-index: 2000;
  top: 0;
  left: 0;
  margin: 1rem;
}

.fullscreen-button img {
  width: 2rem;
}
.fullscreen-z {
  z-index: 1000;
}

.scan-confirmation {
  position: absolute;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.8);
  display: flex;
  flex-flow: row nowrap;
  justify-content: center;
}

</style>